"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { BadgeCheck, Download, LogOut, Mail, MoreHorizontal, Pencil, UserX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownItem, DropdownMenu, DropdownSeparator } from "@/components/ui/dropdown-menu";
import { confirmDialog } from "@/components/ui/confirm";
import { toast } from "@/components/ui/toast";
import type { ContactView } from "@/lib/views";
import { MitgliedDialog } from "./mitglied-dialog";
import { austrittErfassen } from "@/app/(app)/mitglieder/actions";

export function MitgliedAktionen({ kontakt }: { kontakt: ContactView }) {
  const router = useRouter();
  const [bearbeiten, setBearbeiten] = useState(false);

  async function austritt() {
    const ok = await confirmDialog({
      title: `Austritt für ${kontakt.displayName} erfassen?`,
      description: "Status wird „Ausgetreten“, laufende Beiträge enden zum Austrittsdatum. Die Historie bleibt erhalten.",
      confirmLabel: "Austritt erfassen",
    });
    if (!ok) return;
    const ergebnis = await austrittErfassen(kontakt.id);
    if ("error" in ergebnis) {
      toast.error(ergebnis.error);
      return;
    }
    toast.success("Austritt erfasst");
    router.refresh();
  }

  async function anonymisieren() {
    const ok = await confirmDialog({
      title: "Personendaten anonymisieren?",
      description:
        "Name, Anschrift und Kontaktdaten werden unwiderruflich entfernt. Buchungen und Belege bleiben für die Aufbewahrungsfrist mit anonymem Bezug bestehen.",
      confirmLabel: "Anonymisieren",
      danger: true,
    });
    // BACKEND: DSGVO-Loeschung mit Aufbewahrungspruefung JE DOKUMENTKLASSE aus lib/rechtswerte
    // (Buchungsbelege RW.AUFBEWAHRUNG_BUCHUNGSBELEGE_JAHRE, Buecher/Abschluesse RW.AUFBEWAHRUNG_BUECHER_JAHRE,
    // sonstige RW.AUFBEWAHRUNG_SONSTIGE_JAHRE), AuditLog.
    if (ok) toast.success("Daten anonymisiert");
  }

  return (
    <>
      <Button variant="outline" onClick={() => router.push(`/nachrichten/neu?kontakt=${kontakt.id}`)}>
        <Mail className="h-4 w-4" aria-hidden />
        <span className="max-sm:hidden">Nachricht</span>
      </Button>
      <Button variant="outline" onClick={() => setBearbeiten(true)}>
        <Pencil className="h-4 w-4" aria-hidden />
        <span className="max-sm:hidden">Bearbeiten</span>
      </Button>
      <DropdownMenu
        trigger={
          <Button variant="ghost" size="icon" aria-label="Weitere Aktionen">
            <MoreHorizontal className="h-4 w-4" aria-hidden />
          </Button>
        }
      >
        <DropdownItem onClick={() => router.push(`/mein-bereich/ausweis?kontakt=${kontakt.id}`)}>
          <BadgeCheck className="text-text-tertiary h-4 w-4" aria-hidden />
          Mitgliedsausweis
        </DropdownItem>
        <DropdownItem onClick={() => router.push(`/mitglieder/${kontakt.id}/datenschutz`)}>
          <Download className="text-text-tertiary h-4 w-4" aria-hidden />
          Datenauskunft (Art. 15 DSGVO)
        </DropdownItem>
        <DropdownSeparator />
        <DropdownItem onClick={austritt}>
          <LogOut className="text-text-tertiary h-4 w-4" aria-hidden />
          Austritt erfassen
        </DropdownItem>
        <DropdownItem variant="danger" onClick={anonymisieren}>
          <UserX className="h-4 w-4" aria-hidden />
          Anonymisieren
        </DropdownItem>
      </DropdownMenu>
      <MitgliedDialog open={bearbeiten} onClose={() => setBearbeiten(false)} kontakt={kontakt} />
    </>
  );
}
